import { Comment } from "@prisma/client";
import axios from "axios";
import Blob from "cross-blob";
import {
  Document,
  HeadingLevel,
  ISectionOptions,
  Packer,
  Paragraph,
  TextRun,
} from "docx";
import flattenDeep from "lodash/flattenDeep";

import { prisma } from "@/lib/prisma";

import { docStyles } from "./common";
import { numberingStyle } from "./common/numbering";
import { renderImmigramFormToGoogleDoc } from "../immigramForms";
import { DocGenerator } from "../index";
import { FormWithDataAndConfig } from "../useGenerateDocFromConfigs";

interface EmploymentDocV2Args {
  forms: FormWithDataAndConfig[];
}

export const createEmploymentDocV2: DocGenerator<EmploymentDocV2Args> = async (
  t,
  { forms, application }
) => {
  // Get all comments for the application
  // Try to get comments via API (works both client and server side)
  let comments: Comment[] = [];
  try {
    // First try API (works on both client and server)
    if (typeof window !== "undefined" && process.env.NEXT_PUBLIC_API_URL) {
      const response = await axios.get<Comment[]>(
        `${process.env.NEXT_PUBLIC_API_URL}/comments/${application.id}`
      );
      comments = response.data || [];
      console.log(
        `[EmploymentDocV2] Fetched ${comments.length} comments via API`
      );
    } else if (typeof prisma !== "undefined" && prisma && prisma.comment) {
      // Fallback to prisma on server side
      comments = await prisma.comment.findMany({
        where: {
          entityId: application.id,
          deleted: null,
        },
        orderBy: {
          created: "asc",
        },
      });
      console.log(
        `[EmploymentDocV2] Fetched ${comments.length} comments via Prisma`
      );
    }
  } catch (error) {
    console.warn("Failed to fetch comments for employment doc:", error);
    // Continue without comments if fetching fails
  }

  const document = new Document({
    styles: docStyles,
    numbering: numberingStyle,
    sections: [
      {
        children: flattenDeep<ISectionOptions["children"][0] | null>([
          new Paragraph({
            children: [
              new TextRun({
                text: "Applicant: ",
                bold: true,
                size: 36,
              }),
              new TextRun({
                text: application.applicant.fullName
                  ? application.applicant.fullName
                  : `${application.applicant.firstName} ${application.applicant.lastName}`,
                bold: true,
                size: 36,
              }),
            ],
            heading: HeadingLevel.TITLE,
          }),
          renderImmigramFormToGoogleDoc(t, forms, comments),
        ]).filter((x): x is ISectionOptions["children"][0] => x !== null),
      },
    ],
  });

  return new Blob([await Packer.toBuffer(document)]);
};
